import { useContext } from "react";
import { useNavigate } from "react-router-dom";

import { TApiButtonCommandFn } from ".";

import { welcomeFormSubmittedEvent } from "analytics/onboardingEvents";
import { EstimateFormSlug, FlowTypes } from "config";
import { fetchIneligibleForCoverageReasons } from "gql/queries/account";
import { useCoBranding, useJourney } from "modules";
import { useNavigateToUri } from "modules/hooks/useNavigateToUri";
import { useFlowRouteRedirect } from "router/FlowRoute/useFlowRouteRedirect";
import { getPreVerifiedInitialData } from "store/actions/appLoad";
import { getCurrentUserEstimate } from "store/actions/estimate";
import { finishFlow } from "store/actions/flow";
import { useAppSelector, useAppDispatch } from "store/index";
import replaceWyshwareUri from "utils/replaceWyshwareUri";

import { find, get } from "lodash-es";

import { DynamicFormContext } from "../DynamicFormContext";

export const useFinishFlow = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const navigateToUri = useNavigateToUri();
  const flowTracker = useAppSelector(state => state.analyticsTracker);
  const flowSlug = flowTracker.slug as FlowTypes;
  const finishFlowRedirect = useFinishFlowRedirect(flowSlug);
  const { anyJourneysActive, exitJourney } = useJourney();

  const finishFlowAnalytics = () => dispatch(finishFlow({ flowTracker }));

  const finishFlowCommand: TApiButtonCommandFn = async commandArgs => {
    const linkArgument = (commandArgs || []).find(argument => argument.name === "link");

    finishFlowAnalytics();

    if (linkArgument) {
      const linkPath = replaceWyshwareUri(linkArgument.source);

      if (/^https?:\/\//i.test(linkPath)) {
        navigateToUri(linkPath);
      } else if (anyJourneysActive) {
        navigate(linkPath, { replace: true, state: { ignoreAction: true } });
        exitJourney();
      } else {
        navigate(linkPath, { state: { ignoreAction: true } });
      }
    } else {
      await finishFlowRedirect();
    }
  };

  return finishFlowCommand;
};

const useFinishFlowRedirect = (flowSlug: FlowTypes) => {
  const { nextStepCallback } = useJourney();
  const { redirectToNextPage } = useFlowRouteRedirect(flowSlug);

  const finishEstimateFlow = useFinishEstimateFlow();
  const finishReferralPartnerFlow = useFinishReferralPartnerFlow();
  const finishPreVerifiedInitialDataFlow = useFinishPreVerifiedInitialDataFlow();

  const actions: Partial<Record<FlowTypes, () => PromiseLike<void>>> = {
    [EstimateFormSlug]: finishEstimateFlow,
    "referral-partner": finishReferralPartnerFlow,
    "pre-verified-initial-data": finishPreVerifiedInitialDataFlow,
  };

  const finishFlowCallback = actions[flowSlug];

  const finishFlowRedirect = async () => {
    if (finishFlowCallback) {
      await finishFlowCallback();
    } else {
      await nextStepCallback(redirectToNextPage, false)();
    }
  };

  return finishFlowRedirect;
};

const useFinishPreVerifiedInitialDataFlow = () => {
  const dispatch = useAppDispatch();

  const finishPreVerifiedInitialDataFlow = async () => {
    await dispatch(getPreVerifiedInitialData());
  };

  return finishPreVerifiedInitialDataFlow;
};

const useFinishEstimateFlow = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { coBrandMetadata } = useCoBranding();
  const { inputs } = useContext(DynamicFormContext);

  const finishEstimateFlow = async () => {
    dispatch(welcomeFormSubmittedEvent());

    const coverageInput = find(inputs, { key: "desired-coverage-amount" });

    const response = await fetchIneligibleForCoverageReasons();
    const reasons = get(response, "data.userIneligibleReasons.ineligibleCoverageReasons", []);

    const { latestEstimateDatum } = await dispatch(getCurrentUserEstimate()).unwrap();

    if (reasons.length) {
      const reason = reasons[0];

      navigate(`/ineligible/${reason}`);
    } else if (coBrandMetadata) {
      navigate(`/getting-coverage`);
    } else if (!latestEstimateDatum || coverageInput?.value === "null") {
      navigate("/products/insurance/wysh-builder/list");
    } else {
      navigate("/coverage-preview");
    }
  };

  return finishEstimateFlow;
};

const useFinishReferralPartnerFlow = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { inputs } = useContext(DynamicFormContext);

  const finishReferralPartnerFlow = async () => {
    const coverageInput = find(inputs, { key: "desired-coverage-amount" });
    const { latestEstimateDatum } = await dispatch(getCurrentUserEstimate()).unwrap();

    if (!latestEstimateDatum || coverageInput?.value === "null") {
      navigate("/products/insurance/wysh-builder/list", { replace: true });
    } else {
      navigate("/coverage-preview", { replace: true });
    }
  };

  return finishReferralPartnerFlow;
};
